import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, FileText, BarChart3, Star, Eye, EyeOff, MessageCircle } from 'lucide-react';
import { blogService } from '../services/blogService';
import { reviewService } from '../services/reviewService';
import { replyService } from '../services/replyService';
import './Admin.css';

const Admin = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Trading Insights'
  });
  const [newReply, setNewReply] = useState({
    admin_name: 'Admin',
    reply_content: ''
  });
  const [submittingReply, setSubmittingReply] = useState(false);

  const categories = [
    'Trading Insights',
    'Educational',
    'Cryptocurrency',
    'Portfolio Management',
    'Technical Analysis',
    'Risk Management'
  ];

  // Load posts, reviews, and replies on component mount
  useEffect(() => {
    loadPosts();
    loadReviews();
    loadReplies();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await blogService.getAllPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const data = await reviewService.getAllReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const loadReplies = async () => {
    try {
      const data = await replyService.getAllReplies();
      setReplies(data);
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  // Calculate review statistics
  const totalReviews = reviews.length;
  const publishedReviews = reviews.filter(review => review.published).length;
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0;
  const totalHelpfulVotes = reviews.reduce((sum, review) => sum + (review.helpful || 0), 0);

  const handleNewPost = async () => {
    try {
      setLoading(true);
      const postData = {
        ...newPost,
        author_id: null,
        published: true
      };
      
      await blogService.createPost(postData);
      setNewPost({ title: '', excerpt: '', content: '', category: 'Trading Insights' });
      setShowNewPostModal(false);
      loadPosts(); // Reload posts
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogService.deletePost(id);
        loadPosts(); // Reload posts
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleToggleReviewStatus = async (reviewId, currentStatus) => {
    try {
      await reviewService.updateReview(reviewId, { published: !currentStatus });
      await loadReviews(); // Reload reviews to get updated data
    } catch (error) {
      console.error('Error updating review status:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await reviewService.deleteReview(reviewId);
        await loadReviews(); // Reload reviews
      } catch (error) {
        console.error('Error deleting review:', error);
      }
    }
  };

  const handleReplySubmit = async () => {
    if (!newReply.reply_content.trim() || !selectedReview) {
      alert('Please enter a reply message');
      return;
    }

    try {
      setSubmittingReply(true);
      
      const replyData = {
        review_id: selectedReview.id,
        admin_name: newReply.admin_name.trim() || 'Admin',
        reply_content: newReply.reply_content.trim()
      };

      console.log('Submitting reply:', replyData); // Debug log
      
      await replyService.createReply(replyData);
      
      // Reset form and close modal
      setNewReply({ admin_name: 'Admin', reply_content: '' });
      setShowReplyModal(false);
      setSelectedReview(null);
      
      // Reload replies to show the new one
      await loadReplies();
      
      console.log('Reply submitted successfully'); // Debug log
      
    } catch (error) {
      console.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (window.confirm('Are you sure you want to delete this reply?')) {
      try {
        await replyService.deleteReply(replyId);
        await loadReplies();
      } catch (error) {
        console.error('Error deleting reply:', error);
      }
    }
  };

  return (
    <div className="admin">
      {/* Admin Header */}
      <section className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <button className="logout-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </section>

      {/* Admin Navigation */}
      <section className="admin-nav">
        <div className="container">
          <div className="admin-nav-buttons">
            <button
              className={`admin-nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <BarChart3 className="nav-icon" />
              Dashboard
            </button>
            <button
              className={`admin-nav-btn ${activeTab === 'blog' ? 'active' : ''}`}
              onClick={() => setActiveTab('blog')}
            >
              <FileText className="nav-icon" />
              Blog Management
            </button>
            <button
              className={`admin-nav-btn ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              <Star className="nav-icon" />
              Review Management
            </button>
          </div>
        </div>
      </section>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <section className="section">
          <div className="container">
            <div className="dashboard-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <FileText />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{posts.length}</div>
                  <div className="stat-label">Blog Posts</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <Star />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{totalReviews}</div>
                  <div className="stat-label">Total Reviews</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Review Management Tab */}
      {activeTab === 'reviews' && (
        <section className="section">
          <div className="container">
            <div className="review-management-header">
              <h3>Review Management</h3>
              <div className="review-stats-summary">
                <div className="stat-item">
                  <span className="stat-value">{totalReviews}</span>
                  <span className="stat-label">Total Reviews</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{publishedReviews}</span>
                  <span className="stat-label">Published</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{replies.length}</span>
                  <span className="stat-label">Replies</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{averageRating}</span>
                  <span className="stat-label">Avg Rating</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{totalHelpfulVotes}</span>
                  <span className="stat-label">Helpful Votes</span>
                </div>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Loading reviews...</div>
            ) : (
              <div className="reviews-management-list">
                {reviews.map(review => (
                  <div key={review.id} className="review-management-item">
                    <div className="review-info">
                      <div className="review-header">
                        <div className="review-user">
                          <strong>{review.user_name}</strong>
                          <div className="review-rating">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                size={16} 
                                className={i < review.rating ? 'filled' : 'empty'} 
                              />
                            ))}
                          </div>
                        </div>
                        <div className="review-meta">
                          <span className="review-date">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                          <span className={`review-status ${review.published ? 'published' : 'pending'}`}>
                            {review.published ? 'Published' : 'Pending'}
                          </span>
                        </div>
                      </div>
                      <div className="review-content">
                        <h4>{review.title}</h4>
                        <p>{review.content}</p>
                      </div>
                      <div className="review-stats">
                        <span className="helpful-count">
                           {review.helpful || 0} helpful
                        </span>
                        <span className="verified-status">
                          {review.verified ? '✓ Verified' : '○ Unverified'}
                        </span>
                      </div>
                      
                      {/* Show replies for this review */}
                      {replies.filter(reply => reply.review_id === review.id).length > 0 && (
                        <div className="review-replies">
                          <h5>Admin Replies:</h5>
                          {replies
                            .filter(reply => reply.review_id === review.id)
                            .map(reply => (
                              <div key={reply.id} className="reply-item">
                                <div className="reply-header">
                                  <strong>{reply.admin_name}</strong>
                                  <span className="reply-date">
                                    {new Date(reply.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="reply-content">{reply.reply_content}</p>
                                <button 
                                  className="delete-reply-btn"
                                  onClick={() => handleDeleteReply(reply.id)}
                                >
                                  <Trash2 size={14} />
                                  Delete Reply
                                </button>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    <div className="review-actions">
                      <button 
                        className="action-btn reply"
                        onClick={() => {
                          setSelectedReview(review);
                          setShowReplyModal(true);
                        }}
                      >
                        <MessageCircle className="action-icon" />
                        Reply
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteReview(review.id)}
                      >
                        <Trash2 className="action-icon" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Blog Management Tab */}
      {activeTab === 'blog' && (
        <section className="section">
          <div className="container">
            <div className="blog-management-header">
              <h3>Blog Management</h3>
              <button 
                className="btn btn-primary"
                onClick={() => setShowNewPostModal(true)}
                disabled={loading}
              >
                <Plus className="btn-icon" />
                New Post
              </button>
            </div>
            
            {loading ? (
              <div className="loading">Loading posts...</div>
            ) : (
              <div className="blog-posts-list">
                {posts.map(post => (
                  <div key={post.id} className="blog-post-item">
                    <div className="post-info">
                      <h4>{post.title}</h4>
                      <p>Published on {new Date(post.created_at).toLocaleDateString()}</p>
                      <span className={`post-status ${post.published ? 'published' : 'draft'}`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <div className="post-actions">
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        <Trash2 className="action-icon" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* New Post Modal */}
      {showNewPostModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Blog Post</h3>
              <button 
                className="modal-close"
                onClick={() => setShowNewPostModal(false)}
              >
                <X />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  placeholder="Enter post title"
                />
              </div>
              
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Excerpt</label>
                <textarea
                  value={newPost.excerpt}
                  onChange={(e) => setNewPost({...newPost, excerpt: e.target.value})}
                  placeholder="Enter post excerpt"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  placeholder="Enter post content"
                  rows="8"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-outline"
                onClick={() => setShowNewPostModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleNewPost}
                disabled={loading}
              >
                <Save className="btn-icon" />
                {loading ? 'Publishing...' : 'Publish Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedReview && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Reply to Review</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowReplyModal(false);
                  setSelectedReview(null);
                  setNewReply({ admin_name: 'Admin', reply_content: '' });
                }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="review-preview">
                <h4>Review by {selectedReview.user_name}</h4>
                <p>"{selectedReview.content}"</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="admin_name">Admin Name</label>
                <input
                  type="text"
                  id="admin_name"
                  value={newReply.admin_name}
                  onChange={(e) => setNewReply({...newReply, admin_name: e.target.value})}
                  placeholder="Enter admin name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="reply_content">Reply Content</label>
                <textarea
                  id="reply_content"
                  value={newReply.reply_content}
                  onChange={(e) => setNewReply({...newReply, reply_content: e.target.value})}
                  placeholder="Write your reply..."
                  rows={5}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowReplyModal(false);
                  setSelectedReview(null);
                  setNewReply({ admin_name: 'Admin', reply_content: '' });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleReplySubmit}
                disabled={submittingReply || !newReply.reply_content.trim()}
              >
                {submittingReply ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 